import { Atom, Braces, ChevronDown, ChevronRight, Code, Copy, Download, File, FileText, Folder, Hash, RefreshCw, Settings, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import shadcn from "../../assets/docs/shadcn.json";
import UtilsService from "../../services/api/Utils";
import AgentService from "../../services/api/agents";
import { showNotificationGlobal } from "../common/NotificationProvider";

// Monaco Editor
import Editor, { useMonaco } from "@monaco-editor/react";
import { FaLock } from "react-icons/fa";

// SCM / Connectors integration rendered in the main panel.
import SCMIntegrationPage from "./SCMIntegrationPage";

// Helper function for file icons (extracted for reusability)
const getFileIconByExtension = (filename, size = 14) => {
  if (!filename) return <FileText size={size} />;
  
  const ext = filename.split('.').pop().toLowerCase();
  switch (ext) {
    case 'css':
      return <Hash size={size} />;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return <Atom size={size} />;
    case 'json':
      return <Braces size={size} />;
    default:
      return <FileText size={size} />;
  }
};

// Tab context menu — VS Code-style actions for open file tabs
const TabContextMenu = React.memo(({ x, y, onCopyPath, onClose, onCloseOthers, onCloseAll, onDismiss }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onDismiss();
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", onDismiss, true);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", onDismiss, true);
    };
  }, [onDismiss]);

  return (
    <div
      ref={menuRef}
      className="code-tab-context-menu"
      style={{ top: y, left: x }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      <button type="button" className="code-tab-context-menu-item" role="menuitem" onClick={onCopyPath}>
        Copy Path
      </button>
      <div className="code-tab-context-menu-separator" role="separator" />
      <button type="button" className="code-tab-context-menu-item" role="menuitem" onClick={onClose}>
        Close
      </button>
      <button type="button" className="code-tab-context-menu-item" role="menuitem" onClick={onCloseOthers}>
        Close Others
      </button>
      <button type="button" className="code-tab-context-menu-item" role="menuitem" onClick={onCloseAll}>
        Close All
      </button>
    </div>
  );
});

TabContextMenu.displayName = "TabContextMenu";

// Tab Component - Memoized to prevent unnecessary re-renders
const Tab = React.memo(({ file, isActive, onSelect, onClose, onContextMenu }) => {
  const handleSelect = useCallback(() => {
    onSelect(file);
  }, [file, onSelect]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    onClose(file);
  }, [file, onClose]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, file);
  }, [file, onContextMenu]);

  const filePath = file.fullPath || file.path?.split(" > ").join("/") || file.name;

  return (
    <div 
      className={`tab ${isActive ? 'active' : ''}`}
      onClick={handleSelect}
      onContextMenu={handleContextMenu}
      title={filePath}
    >
      <div className="tab-icon">
        {getFileIconByExtension(file.name)}
      </div>
      <span className="tab-name">
        {file.name}
      </span>
      <button 
        className="tab-close"
        onClick={handleClose}
        title="Close"
        aria-label={`Close ${file.name}`}
      >
        <X size={12} />
      </button>
    </div>
  );
});

Tab.displayName = 'Tab';

// Breadcrumb Component - Memoized to prevent unnecessary re-renders
const Breadcrumbs = React.memo(({ path, onBreadcrumbClick }) => {
  // Call hooks before any early returns
  const pathSegments = useMemo(() => {
    if (!path) return [];
    return path.split(' > ');
  }, [path]);
  
  // Early return after hooks
  if (!path || pathSegments.length === 0) return null;
  
  return (
    <div className="breadcrumbs">
      {pathSegments.map((segment, index) => {
        const isLast = index === pathSegments.length - 1;
        const segmentPath = pathSegments.slice(0, index + 1).join(' > ');
        
        return (
          <React.Fragment key={segmentPath}>
            <span 
              className={`breadcrumb-item ${isLast ? 'active' : 'clickable'}`}
              onClick={!isLast ? () => onBreadcrumbClick(segmentPath) : undefined}
            >
              {segment}
            </span>
            {!isLast && <span className="breadcrumb-separator">{'>'}</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
});

Breadcrumbs.displayName = 'Breadcrumbs';

// Editor theme options for Monaco (value is Monaco theme id)
const EDITOR_THEMES = [
  { value: "vs-dark", label: "Dark", description: "Default dark" },
  { value: "vs", label: "Light", description: "Default light" },
  { value: "hc-black", label: "High contrast", description: "High contrast dark" },
];

const FONT_SIZES = [12, 14, 16, 18, 20, 22, 24];

const FONT_FAMILIES = [
  { value: "Fira Code", label: "Fira Code" },
  { value: "JetBrains Mono", label: "JetBrains Mono" },
  { value: "Source Code Pro", label: "Source Code Pro" },
  { value: "Monaco", label: "Monaco" },
  { value: "Consolas", label: "Consolas" },
  { value: "Menlo", label: "Menlo" },
  { value: "SF Mono", label: "SF Mono" },
];

// Build a CSS font stack so Monaco always has a valid monospace fallback
/** Notify main chat / other surfaces of the latest code file list (name + contents). */
function broadcastCodeviewFiles(codes) {
  const files = Array.isArray(codes)
    ? codes
        .filter((item) => item && item.name)
        .map((item) => ({
          name: item.name,
          contents: item.contents ?? item.contentnts ?? "",
        }))
    : [];
  window.dispatchEvent(
    new CustomEvent("codeview-files-updated", { detail: { files } })
  );
}

const getEditorFontFamily = (fontFamily) => {
  const quoted = fontFamily.includes(" ") ? `'${fontFamily}'` : fontFamily;
  return `${quoted}, Monaco, Menlo, 'Ubuntu Mono', Consolas, 'Courier New', monospace`;
};

// Editor Header with Copy and Download only (settings moved to sidebar)
const EditorHeader = React.memo(({ activeFile, onCopy, onDownload }) => {
  if (!activeFile) return null;

  return (
    <div className="editor-header">
      <div className="editor-actions">
        <button 
          className="editor-action-btn"
          onClick={onCopy}
          title="Copy to clipboard"
        >
          <Copy size={16} />
        </button>
        <button 
          className="editor-action-btn"
          onClick={onDownload}
          title="Download file"
        >
          <Download size={16} />
        </button>
      </div>
    </div>
  );
});

EditorHeader.displayName = 'EditorHeader';

const CONTENT_KEYS = [
  "contents",
  "contentnts",
  "content",
  "fileContent",
  "fileContents",
  "file_content",
  "source",
  "value",
];

const createTreeNode = ({
  name,
  fullPath,
  isFile,
  content = null,
  binary = false,
  sizeExceeded = false,
  children = [],
  isLoaded = false,
  isLoading = false,
}) => ({
  name,
  fullPath,
  content,
  binary,
  sizeExceeded,
  children,
  isFile,
  isLoaded,
  isLoading,
});

const getCodeviewPayload = (response) => {
  if (response?.data?.data !== undefined) return response.data.data;
  return response?.data ?? null;
};

const hasKnownContent = (value) => typeof value === "string";

const getKnownContent = (value) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return null;

  for (const key of CONTENT_KEYS) {
    if (hasKnownContent(value[key])) {
      return value[key];
    }
  }

  return null;
};

const isListingPayload = (payload) =>
  Boolean(
    payload &&
      typeof payload === "object" &&
      (Array.isArray(payload.folders) || Array.isArray(payload.files))
  );

const buildDisplayPath = (fullPath) => (fullPath ? fullPath.split("/").join(" > ") : "");

const createNodeFromListingEntry = (entry, basePath = "") => {
  if (!entry) return null;

  if (typeof entry === "string") {
    const fullPath = basePath ? `${basePath}/${entry}` : entry;
    return createTreeNode({
      name: entry,
      fullPath,
      isFile: false,
      isLoaded: false,
    });
  }

  const name = entry.name;
  if (!name) return null;

  const fullPath = basePath ? `${basePath}/${name}` : name;
  const content = getKnownContent(entry);
  const isFile = !entry.isDir;

  return createTreeNode({
    name,
    fullPath,
    isFile,
    content,
    binary: Boolean(entry.binary),
    sizeExceeded: Boolean(entry.sizeExceeded),
    isLoaded: isFile ? content !== null : false,
  });
};

const buildTreeFromListing = (payload, basePath = "") => {
  if (!isListingPayload(payload)) return [];

  const folderNames = new Set();

  (payload.folders || []).forEach((entry) => {
    const name = typeof entry === "string" ? entry : entry?.name;
    if (name) folderNames.add(name);
  });

  (payload.files || []).forEach((entry) => {
    if (entry?.isDir && entry?.name) {
      folderNames.add(entry.name);
    }
  });

  const folders = [...folderNames]
    .map((name) =>
      createTreeNode({
        name,
        fullPath: basePath ? `${basePath}/${name}` : name,
        isFile: false,
        isLoaded: false,
      })
    )
    .sort(sortTreeNodes);

  const files = (payload.files || [])
    .filter((entry) => entry && !entry.isDir)
    .map((entry) => createNodeFromListingEntry(entry, basePath))
    .filter(Boolean)
    .sort(sortTreeNodes);

  return [...folders, ...files];
};

// Build tree structure from legacy flat file array
const buildTreeFromFiles = (files) => {
  const tree = {};
  const rootNodes = [];

  files.forEach((file) => {
    if (!file?.name) return;

    const pathParts = file.name.split("/");
    const fullPath = file.name;
    tree[fullPath] = createTreeNode({
      name: pathParts[pathParts.length - 1],
      fullPath,
      isFile: pathParts.length === 1 || !file.name.includes("/"),
      content: getKnownContent(file),
      binary: Boolean(file.binary),
      sizeExceeded: Boolean(file.sizeExceeded),
      children: [],
      isLoaded: true,
    });
  });

  Object.values(tree).forEach((node) => {
    const pathParts = node.fullPath.split("/");

    if (pathParts.length === 1) {
      rootNodes.push(node);
      return;
    }

    let currentPath = "";
    let currentParent = null;

    for (let i = 0; i < pathParts.length - 1; i++) {
      currentPath = currentPath ? `${currentPath}/${pathParts[i]}` : pathParts[i];

      if (!tree[currentPath]) {
        tree[currentPath] = createTreeNode({
          name: pathParts[i],
          fullPath: currentPath,
          isFile: false,
          children: [],
          isLoaded: true,
        });

        if (currentParent) {
          currentParent.children.push(tree[currentPath]);
        } else if (!rootNodes.some((item) => item.fullPath === currentPath)) {
          rootNodes.push(tree[currentPath]);
        }
      }

      currentParent = tree[currentPath];
    }

    if (currentParent && !currentParent.children.some((child) => child.fullPath === node.fullPath)) {
      currentParent.children.push(node);
    }
  });

  return rootNodes.sort(sortTreeNodes);
};

const buildTreeFromPayload = (payload, settingsTab) => {
  if (Array.isArray(payload)) {
    const codes = settingsTab === "frontend" ? [...payload, ...shadcn] : [...payload];
    return buildTreeFromFiles(codes);
  }

  return buildTreeFromListing(payload);
};

// Sort comparator: directories first, then alphabetically
const sortTreeNodes = (a, b) => {
  // Sort directories first, then files
  if (a.isFile !== b.isFile) {
    return a.isFile ? 1 : -1;
  }
  return a.name.localeCompare(b.name);
};

// Detect language from file extension for Monaco Editor
const getLanguageFromFilename = (filename) => {
  if (!filename) return "javascript";

  const ext = filename.split(".").pop().toLowerCase();
  switch (ext) {
    case "js":
    case "jsx":
      return "javascript";
    case "ts":
    case "tsx":
      return "typescript";
    case "html":
      return "html";
    case "css":
      return "css";
    case "json":
      return "json";
    case "py":
      return "python";
    case "java":
      return "java";
    default:
      return "plaintext";
  }
};

const buildSubtreeFromLegacyFiles = (files, folderPath) => {
  if (!Array.isArray(files)) return [];

  const prefix = folderPath ? `${folderPath}/` : "";
  const scopedFiles = files
    .filter((file) => file?.name?.startsWith(prefix))
    .map((file) => ({
      ...file,
      name: file.name.slice(prefix.length),
    }))
    .filter((file) => file.name);

  return buildTreeFromFiles(scopedFiles);
};

// Helper to find node by path in tree
const findNodeByPath = (nodes, targetPath) => {
  for (const node of nodes) {
    if (node.fullPath === targetPath) {
      return node;
    }
    if (node.children) {
      const found = findNodeByPath(node.children, targetPath);
      if (found) return found;
    }
  }
  return null;
};

const updateNodeInTree = (nodes, targetPath, updater) =>
  nodes.map((node) => {
    if (node.fullPath === targetPath) {
      return updater(node);
    }

    if (!Array.isArray(node.children) || node.children.length === 0) {
      return node;
    }

    return {
      ...node,
      children: updateNodeInTree(node.children, targetPath, updater),
    };
  });

const collectLoadedFiles = (nodes) =>
  nodes.flatMap((node) => {
    const current =
      node.isFile && node.content !== null
        ? [{ name: node.fullPath, contents: node.content ?? "" }]
        : [];
    const nested = Array.isArray(node.children) ? collectLoadedFiles(node.children) : [];
    return [...current, ...nested];
  });

const extractFilePayload = (payload, requestedPath) => {
  if (payload == null) return null;

  if (typeof payload === "string") {
    return { name: requestedPath, content: payload, binary: false, sizeExceeded: false };
  }

  if (Array.isArray(payload)) {
    const match =
      payload.find((item) => item?.name === requestedPath) ||
      payload.find((item) => item?.path === requestedPath) ||
      payload.find((item) => {
        const name = item?.name || item?.path || "";
        const req = String(requestedPath || "").replace(/\\/g, "/");
        const n = String(name).replace(/\\/g, "/");
        return n === req || n.endsWith("/" + req) || req.endsWith("/" + n) ||
          n.split("/").pop() === req.split("/").pop();
      }) ||
      payload.find((item) => item?.name === requestedPath.split("/").pop());

    if (!match) return null;

    const content = getKnownContent(match);
    if (content === null) return null;

    return {
      name: match.name || match.path || requestedPath,
      content,
      binary: Boolean(match.binary),
      sizeExceeded: Boolean(match.sizeExceeded),
    };
  }

  if (Array.isArray(payload.files)) {
    const match =
      payload.files.find((item) => item?.name === requestedPath) ||
      payload.files.find((item) => item?.path === requestedPath) ||
      payload.files.find((item) => {
        const name = item?.name || item?.path || "";
        const req = String(requestedPath || "").replace(/\\/g, "/");
        const n = String(name).replace(/\\/g, "/");
        return n === req || n.endsWith("/" + req) || req.endsWith("/" + n) ||
          n.split("/").pop() === req.split("/").pop();
      }) ||
      payload.files.find((item) => item?.name === requestedPath.split("/").pop());

    if (match) {
      const content = getKnownContent(match);
      if (content !== null) {
        return {
          name: match.path || match.name || requestedPath,
          content,
          binary: Boolean(match.binary),
          sizeExceeded: Boolean(match.sizeExceeded),
        };
      }
    }
  }

  const content = getKnownContent(payload);
  if (content === null) return null;

  return {
    name: payload.path || payload.name || payload.file || requestedPath,
    content,
    binary: Boolean(payload.binary),
    sizeExceeded: Boolean(payload.sizeExceeded),
  };
};

// Helper function for tree node icons
const getTreeNodeIcon = (node) => {
  if (!node.isFile) {
    return <Folder size={16} />;
  }
  
  const ext = node.name.split('.').pop().toLowerCase();
  switch (ext) {
    case 'css':
      return <Hash size={16} />;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return <Atom size={16} />;
    case 'json':
      return <Braces size={16} />;
    default:
      return <File size={16} />;
  }
};

// Recursive Tree Component - Memoized for performance
const TreeNode = React.memo(({ node, onFileClick, onFolderClick, isSelected = false, selectedFile, level = 0 }) => {
  const [expanded, setExpanded] = useState(
    level === 0 && Array.isArray(node.children) && node.children.length > 0
  );
  const isFolder = !node.isFile;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  const handleClick = useCallback(() => {
    if (isFolder) {
      const nextExpanded = !expanded;
      setExpanded(nextExpanded);
      if (nextExpanded) {
        onFolderClick?.(node);
      }
      return;
    }

    onFileClick({
      ...node,
      path: buildDisplayPath(node.fullPath),
      content: node.content,
    });
  }, [expanded, isFolder, node, onFileClick, onFolderClick]);

  const sortedChildren = useMemo(() => {
    if (!hasChildren) return [];
    return [...node.children].sort(sortTreeNodes);
  }, [node.children, hasChildren]);

  return (
    <div className="tree-node" style={{ marginLeft: `${level * 16}px` }}>
      <div 
        className={`tree-label ${isSelected ? 'selected' : ''}`} 
        onClick={handleClick}
        style={{ cursor: "pointer" }}
      >
        <div className="tree-item">
          {isFolder && (
            <div className="tree-icon">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          )}
          <div className="file-icon">
            {getTreeNodeIcon(node)}
          </div>
          <span className="file-name" title={node.fullPath || node.name}>
            {node.name}
          </span>
        </div>
      </div>

      {isFolder && expanded && node.isLoading && (
        <div className="tree-children" style={{ marginLeft: "16px", opacity: 0.7 }}>
          <div className="tree-inline-loading" role="status" aria-live="polite">
            <RefreshCw size={12} className="tree-inline-loading-icon" aria-hidden="true" />
            <span>Loading</span>
          </div>
        </div>
      )}

      {isFolder && expanded && hasChildren && (
        <div className="tree-children">
          {sortedChildren.map((child) => (
            <TreeNode 
              key={child.fullPath} 
              node={child} 
              onFileClick={onFileClick}
              onFolderClick={onFolderClick}
              isSelected={selectedFile?.fullPath === child.fullPath}
              selectedFile={selectedFile}
              level={level + 1}
            />
          ))}
        </div>
      )}

      {isFolder && expanded && node.isLoaded && !node.isLoading && !hasChildren && (
        <div className="tree-children" style={{ marginLeft: "16px", opacity: 0.7 }}>
          Empty
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = "TreeNode";

const STORAGE_PATH_KEYS = [
  "storagePath",
  "storage_path",
  "path",
  "filePath",
  "file_path",
  "location",
  "objectKey",
  "object_key",
];

function extractStoragePath(payload, depth = 0) {
  if (payload == null || depth > 3) return "";
  if (typeof payload === "string") {
    const text = payload.trim();
    if (!text || /^(success|ok|failed|failure)$/i.test(text)) return "";
    return text.includes("/") ? text : "";
  }
  if (typeof payload !== "object") return "";
  for (const key of STORAGE_PATH_KEYS) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  for (const key of ["data", "result", "payload", "body"]) {
    const nested = extractStoragePath(payload[key], depth + 1);
    if (nested) return nested;
  }
  return "";
}

const getCodeview = async (projectId, userId, settingsTab, options = {}) => {
  if (!projectId || !userId) return null;
  try {
    const response = await UtilsService.getCodeview(projectId, userId, settingsTab, options);
    return getCodeviewPayload(response);
  } catch (error) {
    console.error('Error fetching code view:', error);
    throw error;
  }
};
const DEFAULT_EDITOR_SETTINGS = {
  fontSize: 12,
  fontFamily: "Fira Code",
  theme: "vs",
};

function CodeViewComponent({
  projectId,
  projectName,
  projectMasterUser,
  codeRefreshTrigger,
  codeDiscardTrigger,
  discardedFilePaths,
  getCodes,
  onEditorChange,
  onDevelopModeChange,
  // ─── GitHub view ─────────────────────────────────────────────────
  // Controlled from the parent (wherever the "GitHub" pill button
  // lives). When true, the main panel shows SCMIntegrationPage
  // instead of the file editor / "No Files Open" empty state. The
  // sidebar (file tree, Frontend/Backend tabs) is untouched either way.
  connectorsViewActive = false,
  onExitConnectorsView,
  sourceCompressTrigger = 0,
  onCompressingChange,
}) {
  const [openFiles, setOpenFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [compressingSource, setCompressingSource] = useState(false);
  const [sourceArchive, setSourceArchive] = useState(null);
  const [tabContextMenu, setTabContextMenu] = useState(null); // { x, y, file }
  const [selectionPopover, setSelectionPopover] = useState(null); // { x, y, file, startLine, endLine, text }
  const [isLoadingTree, setIsLoadingTree] = useState(true);
  const [frontendSettings, setFrontendSettings] = useState(DEFAULT_EDITOR_SETTINGS);
  const [backendSettings, setBackendSettings] = useState(DEFAULT_EDITOR_SETTINGS);
  const [settingsTab, setSettingsTab] = useState("frontend"); // 'frontend' | 'backend'
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  // Track which tab the current fetch is for; ignore stale responses (e.g. Frontend result after user switched to Backend)
  const fetchTabRef = useRef("frontend");
  const codeviewCacheRef = useRef({
    cacheKey: null,
    tabs: { frontend: null, backend: null }, // { codes, tree }
  });
  // Add this hook in your component
  const monaco = useMonaco();
  const editorRef = useRef(null);
  const activeFileRef = useRef(null);
  const openFilesRef = useRef([]);
  const prevCodeRefreshTriggerRef = useRef(codeRefreshTrigger);
  const prevCodeDiscardTriggerRef = useRef(codeDiscardTrigger);

  // Which settings apply: driven by sidebar tab (Frontend code / Backend code)
  const currentSettings = useMemo(
    () => (settingsTab === "frontend" ? frontendSettings : backendSettings),
    [settingsTab, frontendSettings, backendSettings]
  );

  useEffect(() => {
    onDevelopModeChange?.(settingsTab);
  }, [onDevelopModeChange, settingsTab]);

  const [treeData, setTreeData] = useState([]);
  const [hasCodeviewAccess, setHasCodeviewAccess] = useState(true);
  const treeDataRef = useRef([]);

  const cacheKey = useMemo(() => {
    if (!projectId || projectMasterUser == null) return null;
    return `${String(projectId)}::${String(projectMasterUser)}`;
  }, [projectId, projectMasterUser]);

  useEffect(() => {
    treeDataRef.current = treeData;
    const loadedFiles = collectLoadedFiles(treeData);
    getCodes?.(loadedFiles);
    broadcastCodeviewFiles(loadedFiles);
  }, [getCodes, treeData]);

  const syncTreeForTab = useCallback((tab, nextTree) => {
    codeviewCacheRef.current.tabs[tab] = { tree: nextTree };
    if (fetchTabRef.current === tab) {
      setTreeData(nextTree);
    }
  }, []);

  const updateTreeForTab = useCallback(
    (tab, updater) => {
      const sourceTree =
        fetchTabRef.current === tab
          ? treeDataRef.current
          : codeviewCacheRef.current.tabs?.[tab]?.tree || [];
      const nextTree = updater(sourceTree || []);
      syncTreeForTab(tab, nextTree);
      return nextTree;
    },
    [syncTreeForTab]
  );

  const syncOpenFileContent = useCallback((fullPath, content) => {
    setOpenFiles((prev) =>
      prev.map((file) =>
        file.fullPath === fullPath ? { ...file, content } : file
      )
    );
    setActiveFile((prev) =>
      prev?.fullPath === fullPath ? { ...prev, content } : prev
    );
  }, []);

  const loadFolderChildren = useCallback(
    async (tab, folderPath) => {
      const currentTree =
        fetchTabRef.current === tab
          ? treeDataRef.current
          : codeviewCacheRef.current.tabs?.[tab]?.tree || [];
      const folderNode = findNodeByPath(currentTree, folderPath);

      if (!folderNode || folderNode.isLoaded || folderNode.isLoading) {
        return;
      }

      updateTreeForTab(tab, (nodes) =>
        updateNodeInTree(nodes, folderPath, (node) => ({
          ...node,
          isLoading: true,
        }))
      );

      try {
        const payload = await getCodeview(projectId, projectMasterUser, tab, {
          path: folderPath,
        });
        const children = Array.isArray(payload)
          ? buildSubtreeFromLegacyFiles(payload, folderPath)
          : buildTreeFromListing(payload, folderPath);

        setHasCodeviewAccess(true);
        updateTreeForTab(tab, (nodes) =>
          updateNodeInTree(nodes, folderPath, (node) => ({
            ...node,
            children,
            isLoaded: true,
            isLoading: false,
          }))
        );
      } catch (error) {
        updateTreeForTab(tab, (nodes) =>
          updateNodeInTree(nodes, folderPath, (node) => ({
            ...node,
            isLoading: false,
          }))
        );
      }
    },
    [projectId, projectMasterUser, updateTreeForTab]
  );

  const loadFileContent = useCallback(
    async (tab, filePath) => {
      const currentTree =
        fetchTabRef.current === tab
          ? treeDataRef.current
          : codeviewCacheRef.current.tabs?.[tab]?.tree || [];
      const fileNode = findNodeByPath(currentTree, filePath);

      if (!fileNode) return null;
      if (fileNode.content !== null) {
        return {
          content: fileNode.content,
          binary: fileNode.binary,
          sizeExceeded: fileNode.sizeExceeded,
        };
      }
      if (fileNode.isLoading) return null;

      updateTreeForTab(tab, (nodes) =>
        updateNodeInTree(nodes, filePath, (node) => ({
          ...node,
          isLoading: true,
        }))
      );

      try {
        const payload = await getCodeview(projectId, projectMasterUser, tab, {
          file: filePath,
        });
        const fileData = extractFilePayload(payload, filePath);

        if (!fileData) {
          updateTreeForTab(tab, (nodes) =>
            updateNodeInTree(nodes, filePath, (node) => ({
              ...node,
              isLoading: false,
            }))
          );
          return null;
        }

        setHasCodeviewAccess(true);
        updateTreeForTab(tab, (nodes) =>
          updateNodeInTree(nodes, filePath, (node) => ({
            ...node,
            content: fileData.content,
            binary: fileData.binary,
            sizeExceeded: fileData.sizeExceeded,
            isLoaded: true,
            isLoading: false,
          }))
        );
        syncOpenFileContent(filePath, fileData.content);
        return fileData;
      } catch (error) {
        updateTreeForTab(tab, (nodes) =>
          updateNodeInTree(nodes, filePath, (node) => ({
            ...node,
            isLoading: false,
          }))
        );
        return null;
      }
    },
    [projectId, projectMasterUser, syncOpenFileContent, updateTreeForTab]
  );

  const loadCodeviewForTab = useCallback(
    async (tab, { force = false, resetOpenFiles = false } = {}) => {
      if (!cacheKey) {
        setIsLoadingTree(false);
        setTreeData([]);
        setHasCodeviewAccess(true);
        if (resetOpenFiles) {
          setOpenFiles([]);
          setActiveFile(null);
          setSelectionPopover(null);
        }
        return;
      }

      if (codeviewCacheRef.current.cacheKey !== cacheKey) {
        codeviewCacheRef.current = {
          cacheKey,
          tabs: { frontend: null, backend: null },
        };
      }

      const cached = codeviewCacheRef.current.tabs?.[tab] || null;
      if (!force && cached?.tree) {
        fetchTabRef.current = tab;
        setIsLoadingTree(false);
        setHasCodeviewAccess(true);
        setTreeData(cached.tree);
        if (resetOpenFiles) {
          setOpenFiles([]);
          setActiveFile(null);
          setSelectionPopover(null);
        }
        return;
      }

      setIsLoadingTree(true);
      fetchTabRef.current = tab;
      const tabForThisFetch = tab;

      try {
        const payload = await getCodeview(projectId, projectMasterUser, tab);
        if (fetchTabRef.current !== tabForThisFetch) return;

        const nextTree = buildTreeFromPayload(payload, tabForThisFetch);
        syncTreeForTab(tabForThisFetch, nextTree);
        setHasCodeviewAccess(true);

        if (resetOpenFiles) {
          setOpenFiles([]);
          setActiveFile(null);
          setSelectionPopover(null);
        }
      } catch (error) {
        if (fetchTabRef.current !== tabForThisFetch) return;

        setTreeData([]);
        if (error?.response?.status === 403) {
          setHasCodeviewAccess(false);
        } else {
          setHasCodeviewAccess(true);
          console.error("Error fetching code view:", error);
        }

        codeviewCacheRef.current.tabs[tabForThisFetch] = null;
        if (resetOpenFiles) {
          setOpenFiles([]);
          setActiveFile(null);
          setSelectionPopover(null);
        }
      } finally {
        if (fetchTabRef.current === tabForThisFetch) {
          setIsLoadingTree(false);
        }
      }
    },
    [cacheKey, projectId, projectMasterUser, syncTreeForTab]
  );

  // Close settings panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    if (settingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsOpen]);

  useEffect(() => {
    if (!monaco) return;
  
    // Disable TypeScript/JavaScript error markers
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true,
      noSuggestionDiagnostics: true,
    });
  
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true,
      noSuggestionDiagnostics: true,
    });
  }, [monaco]);

  useEffect(() => {
    activeFileRef.current = activeFile;
  }, [activeFile]);

  useEffect(() => {
    openFilesRef.current = openFiles;
  }, [openFiles]);

  useEffect(() => {
    loadCodeviewForTab(settingsTab, { force: false, resetOpenFiles: true });
  }, [loadCodeviewForTab, settingsTab, projectId, projectMasterUser]);

  // After Save: reload tree and restore open editors to last-saved content
  useEffect(() => {
    if (!codeRefreshTrigger) return;
    // Skip mount with a stale trigger — initial load already fetches fresh code
    if (prevCodeRefreshTriggerRef.current === codeRefreshTrigger) return;
    prevCodeRefreshTriggerRef.current = codeRefreshTrigger;

    let cancelled = false;

    const revertOpenEditors = async () => {
      await loadCodeviewForTab(settingsTab, { force: true, resetOpenFiles: false });
      if (cancelled) return;

      const freshTree =
        codeviewCacheRef.current.tabs?.[settingsTab]?.tree ||
        treeDataRef.current ||
        [];
      const filesToRevert = [...(openFilesRef.current || [])];

      for (const file of filesToRevert) {
        if (cancelled || !file?.fullPath) continue;

        const treeNode = findNodeByPath(freshTree, file.fullPath);
        if (treeNode && treeNode.content !== null && treeNode.content !== undefined) {
          syncOpenFileContent(file.fullPath, treeNode.content);
          continue;
        }

        // Listing/lazy trees: clear cache and re-fetch file contents from server
        updateTreeForTab(settingsTab, (nodes) =>
          updateNodeInTree(nodes, file.fullPath, (node) => ({
            ...node,
            content: null,
            isLoaded: false,
            isLoading: false,
          }))
        );
        await loadFileContent(settingsTab, file.fullPath);
      }
    };

    revertOpenEditors();
    return () => {
      cancelled = true;
    };
  }, [
    codeRefreshTrigger,
    loadCodeviewForTab,
    loadFileContent,
    updateTreeForTab,
    syncOpenFileContent,
    settingsTab,
  ]);

  // After Reset/Discard: close only tabs with unsaved changes; keep other tabs open
  useEffect(() => {
    if (!codeDiscardTrigger) return;
    if (prevCodeDiscardTriggerRef.current === codeDiscardTrigger) return;
    prevCodeDiscardTriggerRef.current = codeDiscardTrigger;

    const normalizePath = (path) => String(path || "").replace(/\\/g, "/").replace(/^\.\//, "");
    const dirtyPaths = (discardedFilePaths || []).map(normalizePath).filter(Boolean);

    const isDirtyTab = (file) => {
      if (!dirtyPaths.length) return false;
      const tabPath = normalizePath(file?.fullPath || file?.name);
      if (!tabPath) return false;
      return dirtyPaths.some(
        (dirty) =>
          tabPath === dirty ||
          tabPath.endsWith(`/${dirty}`) ||
          dirty.endsWith(`/${tabPath}`)
      );
    };

    setOpenFiles((prev) => {
      const remaining = prev.filter((file) => !isDirtyTab(file));
      setActiveFile((current) => {
        if (current && remaining.some((file) => file.id === current.id)) {
          return current;
        }
        return remaining.length > 0 ? remaining[remaining.length - 1] : null;
      });
      return remaining;
    });
    setSelectionPopover(null);
    setTabContextMenu(null);

    // Refresh tree so reopening a discarded file loads last-saved content
    loadCodeviewForTab(settingsTab, { force: true, resetOpenFiles: false });
  }, [codeDiscardTrigger, discardedFilePaths, loadCodeviewForTab, settingsTab]);

  const handleSettingsTabClick = useCallback((tab) => {
    setSettingsTab(tab);
  }, []);

  const handleRefreshCodeviewClick = useCallback(() => {
    loadCodeviewForTab(settingsTab, { force: true, resetOpenFiles: false });
  }, [loadCodeviewForTab, settingsTab]);

  const handleCompressSource = useCallback(async () => {
    if (!projectId || compressingSource) return;
    setCompressingSource(true);
    try {
      const response = await AgentService.compressSource(projectId);
      if (response?.status === "FAILED" || response?.status === "FAILURE") {
        throw new Error(response?.errorMessage || response?.message || "Couldn't compress the source.");
      }
      const storagePath = extractStoragePath(response);
      setSourceArchive({ path: storagePath });
      const notice = storagePath
        ? `Source archive is ready. Download it from Storage files: ${storagePath}`
        : "Source archive is ready. Download it from Storage files.";
      showNotificationGlobal(notice, "success", "topRight");
    } catch (error) {
      const message = error?.response?.data?.errorMessage
        || error?.response?.data?.message
        || error?.message
        || "Couldn't compress the source. Please try again.";
      showNotificationGlobal(message, "error", "topRight");
    } finally {
      setCompressingSource(false);
    }
  }, [compressingSource, projectId]);

  const sourceCompressTriggerRef = useRef(sourceCompressTrigger);
  useEffect(() => {
    if (sourceCompressTriggerRef.current === sourceCompressTrigger) return;
    sourceCompressTriggerRef.current = sourceCompressTrigger;
    if (!sourceCompressTrigger) return;
    handleCompressSource();
  }, [sourceCompressTrigger, handleCompressSource]);

  useEffect(() => {
    onCompressingChange?.(compressingSource);
  }, [compressingSource, onCompressingChange]);

  const copyStoragePath = useCallback(async () => {
    if (!sourceArchive?.path) return;
    try {
      await navigator.clipboard.writeText(sourceArchive.path);
      showNotificationGlobal("Storage path copied.", "success", "topRight");
    } catch {
      showNotificationGlobal("Couldn't copy the storage path.", "error", "topRight");
    }
  }, [sourceArchive]);

  const handleFolderClick = useCallback(
    (folder) => {
      if (!folder?.fullPath || folder.isLoaded || folder.isLoading) return;
      loadFolderChildren(settingsTab, folder.fullPath);
    },
    [loadFolderChildren, settingsTab]
  );

  const handleFileClick = useCallback(
    async (file) => {
      if (!file?.fullPath) return;

      // Opening a file returns the main panel to the editor.
      if (connectorsViewActive) {
        onExitConnectorsView?.();
      }
      let resolvedFile = {
        ...file,
        path: buildDisplayPath(file.fullPath),
      };

      if (resolvedFile.content === null) {
        const fileData = await loadFileContent(settingsTab, resolvedFile.fullPath);
        if (!fileData) return;

        resolvedFile = {
          ...resolvedFile,
          content: fileData.content,
          binary: fileData.binary,
          sizeExceeded: fileData.sizeExceeded,
        };
      }

      setOpenFiles((prev) => {
        const existingIndex = prev.findIndex((item) => item.fullPath === resolvedFile.fullPath);

        if (existingIndex >= 0) {
          const updatedFile = {
            ...prev[existingIndex],
            ...resolvedFile,
            id: prev[existingIndex].id,
          };
          const updatedFiles = [...prev];
          updatedFiles[existingIndex] = updatedFile;
          setActiveFile(updatedFile);
          return updatedFiles;
        }

        const newFile = { ...resolvedFile, id: Date.now() };
        setActiveFile(newFile);
        return [...prev, newFile];
      });
    },
    [connectorsViewActive, loadFileContent, onExitConnectorsView, settingsTab]
  );

  // Memoized tab select handler
  const handleTabSelect = useCallback((file) => {
    setActiveFile(file);
  }, []);

  // Memoized tab close handler
  const handleTabClose = useCallback((fileToClose) => {
    setOpenFiles(prev => {
      const newOpenFiles = prev.filter(f => f.id !== fileToClose.id);
      
      setActiveFile(current => {
        if (current?.id === fileToClose.id) {
          return newOpenFiles.length > 0 ? newOpenFiles[newOpenFiles.length - 1] : null;
        }
        return current;
      });
      
      return newOpenFiles;
    });
  }, []);

  const dismissTabContextMenu = useCallback(() => {
    setTabContextMenu(null);
  }, []);

  const handleTabContextMenu = useCallback((e, file) => {
    setTabContextMenu({ x: e.clientX, y: e.clientY, file });
    setActiveFile(file);
  }, []);

  const copyTextToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, []);

  const handleTabCopyPath = useCallback(async () => {
    const file = tabContextMenu?.file;
    if (!file) return;
    const filePath = file.fullPath || file.path?.split(" > ").join("/") || file.name;
    await copyTextToClipboard(filePath);
    dismissTabContextMenu();
  }, [tabContextMenu, copyTextToClipboard, dismissTabContextMenu]);

  const handleTabCloseFromMenu = useCallback(() => {
    const file = tabContextMenu?.file;
    if (!file) return;
    handleTabClose(file);
    dismissTabContextMenu();
  }, [tabContextMenu, handleTabClose, dismissTabContextMenu]);

  const handleTabCloseOthers = useCallback(() => {
    const file = tabContextMenu?.file;
    if (!file) return;
    setOpenFiles([file]);
    setActiveFile(file);
    dismissTabContextMenu();
  }, [tabContextMenu, dismissTabContextMenu]);

  const handleTabCloseAll = useCallback(() => {
    setOpenFiles([]);
    setActiveFile(null);
    dismissTabContextMenu();
  }, [dismissTabContextMenu]);

  // Memoized copy to clipboard handler
  const handleCopy = useCallback(async () => {
    if (!activeFile || activeFile.content == null) return;
    
    try {
      const editor = editorRef.current;
      if (editor && typeof editor.getModel === "function") {
        const model = editor.getModel();
        const sel = editor.getSelection?.();
        if (model && sel) {
          const selectedText = model.getValueInRange(sel);
          const hasSelection = selectedText && selectedText.length > 0;
          const startLine = sel.startLineNumber;
          const endLine = sel.endLineNumber;
          const header = `@${activeFile.name} (${startLine} - ${endLine})`;
          const textToCopy = hasSelection ? `${header}\n${selectedText}` : `${header}\n${activeFile.content}`;
          await navigator.clipboard.writeText(textToCopy);
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
          return;
        }
      }

      await navigator.clipboard.writeText(activeFile.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = activeFile.content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [activeFile]);

  // Memoized download file handler
  const handleDownload = useCallback(() => {
    if (!activeFile || activeFile.content == null) return;
    
    const blob = new Blob([activeFile.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = activeFile.name || 'code.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [activeFile]);

  // Memoized breadcrumb click handler
  const handleBreadcrumbClick = useCallback((path) => {
    const targetPath = path.split(" > ").join("/");
    const node = findNodeByPath(treeData, targetPath);
    if (!node) return;

    if (node.isFile) {
      handleFileClick(node);
    } else {
      handleFolderClick(node);
    }
  }, [treeData, handleFileClick, handleFolderClick]);


  const handleEditorChange = useCallback((value) => {
    if (!activeFile) return;
    setOpenFiles(prev =>
      prev.map(f => (f.id === activeFile.id ? { ...f, content: value } : f))
    );
    setActiveFile(prev => (prev ? { ...prev, content: value } : null));
    onEditorChange?.(activeFile?.fullPath, value);
  }, [activeFile, onEditorChange]);


  // Theme class for sidebar & tab bar: dark when editor is vs-dark or hc-black
  const isDarkTheme = currentSettings.theme !== 'vs';
  const codeViewThemeClass = isDarkTheme ? 'code-view-theme-dark' : 'code-view-theme-light';

  const activeTabSettings = settingsTab === "frontend" ? frontendSettings : backendSettings;
  const setActiveTabSettings = settingsTab === "frontend" ? setFrontendSettings : setBackendSettings;
  const noAccess = !isLoadingTree && !hasCodeviewAccess;

  if (noAccess) {
    return (
      <div
        className="w-100 d-flex flex-column align-items-center justify-content-center text-center px-3"
        style={{
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'var(--color-bg-secondary, #f6f8fa)',
        }}
      >
        <FaLock size={56} color="#c3c6cc" style={{  marginBottom: '13px' }} aria-hidden="true" />
        {/* <i className="fa-regular fa-lock" style={{ fontSize: '56px', color: '#8c9199', marginBottom: '16px' }} aria-hidden="true" /> */}
        <h2 style={{ color: '#1f2328', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          You don&apos;t have access to this source
        </h2>
        <p style={{ color: '#696f76', fontSize: '13px', maxWidth: '360px', lineHeight: 1.5 }}>
          The project owner needs to give you access to view files in this project.
        </p>
      </div>
    );
  }

  return (
    <div className={`w-100 ${codeViewThemeClass}`} style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
    <aside className="AgentDesignsidebar">
      {/* Sidebar header: Frontend / Backend tabs + Settings icon */}
      <div className="code-view-sidebar-header" ref={settingsRef}>
        <div className="code-view-sidebar-code-tabs">
          <button
            type="button"
            className={`code-view-sidebar-code-tab ${settingsTab === "frontend" ? "active" : ""}`}
            onClick={() => handleSettingsTabClick("frontend")}
          >
            Frontend
          </button>
          <button
            type="button"
            className={`code-view-sidebar-code-tab ${settingsTab === "backend" ? "active" : ""}`}
            onClick={() => handleSettingsTabClick("backend")}
          >
            Backend
          </button>
        </div>
        <button
          className="editor-action-btn code-view-sidebar-refresh-btn"
          onClick={handleRefreshCodeviewClick}
          title="Refresh"
          disabled={isLoadingTree}
          style={{
            opacity: isLoadingTree ? 0.6 : 1,
            cursor: isLoadingTree ? "not-allowed" : "pointer",
          }}
        >
          <RefreshCw size={16} />
        </button>
        <button
          className={`editor-action-btn editor-settings-btn code-view-sidebar-settings-btn ${settingsOpen ? "active" : ""}`}
          onClick={() => setSettingsOpen((o) => !o)}
          title="Editor settings"
        >
          <Settings size={16} />
        </button>
        {settingsOpen && (
          <div className="editor-settings-panel editor-settings-panel-sidebar">
            <div className="editor-settings-title">Editor settings</div>
            <div className="editor-settings-group">
              <label>Theme</label>
              <select
                value={activeTabSettings.theme}
                onChange={(e) =>
                  setActiveTabSettings((s) => ({ ...s, theme: e.target.value }))
                }
                className="editor-settings-select"
              >
                {EDITOR_THEMES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="editor-settings-group">
              <label>Font size</label>
              <select
                value={activeTabSettings.fontSize}
                onChange={(e) =>
                  setActiveTabSettings((s) => ({
                    ...s,
                    fontSize: Number(e.target.value),
                  }))
                }
                className="editor-settings-select"
              >
                {FONT_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}px
                  </option>
                ))}
              </select>
            </div>
            <div className="editor-settings-group">
              <label>Font family</label>
              <select
                value={activeTabSettings.fontFamily}
                onChange={(e) =>
                  setActiveTabSettings((s) => ({ ...s, fontFamily: e.target.value }))
                }
                className="editor-settings-select"
              >
                {FONT_FAMILIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      {isLoadingTree && treeData.length === 0 ? (
        <div className="tree-loading-state">
          <div className="tree-loading-content">
            <div className="loading-icon-wrapper">
              <div className="loading-folder-animation">
                <Folder className="loading-folder" size={48} />
              </div>
            </div>
            <p className="tree-loading-text">Loading files...</p>
            <div className="loading-progress-bar">
              <div className="loading-progress-fill"></div>
            </div>
            
            <div className="skeleton-tree">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="skeleton-tree-item" style={{ animationDelay: `${item * 0.1}s` }}>
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-text" style={{ width: `${Math.random() * 40 + 60}%` }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div 
          className="tree-view-content"
          style={{
            fontSize: currentSettings.fontSize,
            fontFamily: getEditorFontFamily(currentSettings.fontFamily),
          }}
        >
          {treeData.map((node) => (
            <TreeNode 
              key={node.fullPath} 
              node={node} 
              onFileClick={handleFileClick}
              onFolderClick={handleFolderClick}
              isSelected={activeFile?.fullPath === node.fullPath}
              selectedFile={activeFile}
            />
          ))}
        </div>
      )}
        {!isLoadingTree && treeData.length === 0 && (
          <div className="tree-loading-state">
            <div className="tree-loading-content d-flex flex-column align-items-center justify-content-center gap-2" >
              <i className="fa-regular fa-folder-open fa-3x tree-empty-icon"></i>
              <span className="tree-empty-text">No files or folders</span>
            </div>
          </div>
        )}
    </aside>

    <main className="AgentDesignmain">
      {sourceArchive && (
        <div className="code-archive-banner" role="status">
          <div className="code-archive-copy">
            <strong>Source archive is ready</strong>
            <p>Download it from Storage files{sourceArchive.path ? " using this path." : "."}</p>
            {sourceArchive.path ? <code className="code-archive-path">{sourceArchive.path}</code> : null}
          </div>
          <div className="code-archive-actions">
            {sourceArchive.path ? (
              <button type="button" onClick={copyStoragePath}>Copy path</button>
            ) : null}
            <button type="button" onClick={() => setSourceArchive(null)}>Dismiss</button>
          </div>
        </div>
      )}
      {connectorsViewActive?(
          // ─── Connectors / SCM view ───────────────────────────────────────
          // Takes over the same space as the tab-bar/editor/empty-state.
          // The file tree sidebar and Frontend/Backend tabs stay unchanged.
          
          <div style={{ height: "100%", overflowY: "auto" }} >
            <SCMIntegrationPage
              projectId={projectId}
              projectName={projectName}
              embedded
              onBack={onExitConnectorsView}
            />
          </div>
        ) : openFiles.length > 0 ? (
        <>
          <div className="tab-bar">
            {openFiles.map((file) => (
              <Tab
                key={file.id}
                file={file}
                isActive={activeFile?.id === file.id}
                onSelect={handleTabSelect}
                onClose={handleTabClose}
                onContextMenu={handleTabContextMenu}
              />
            ))}
          </div>

          {tabContextMenu && (
            <TabContextMenu
              x={tabContextMenu.x}
              y={tabContextMenu.y}
              onCopyPath={handleTabCopyPath}
              onClose={handleTabCloseFromMenu}
              onCloseOthers={handleTabCloseOthers}
              onCloseAll={handleTabCloseAll}
              onDismiss={dismissTabContextMenu}
            />
          )}
          
          {activeFile && (
            <div className="editor-header-container">
              <Breadcrumbs 
                path={activeFile.path} 
                onBreadcrumbClick={handleBreadcrumbClick}
              />
              <div className="editor-header-actions-wrapper">
                <EditorHeader 
                  activeFile={activeFile}
                  onCopy={handleCopy}
                  onDownload={handleDownload}
                />
              </div>
            </div>
          )}

          {copySuccess && (
            <div className="copy-notification">
              Code copied to clipboard!
            </div>
          )}

          {activeFile && (
            <div className="editor-container" style={{ position: "relative" }}>
              {selectionPopover?.file && (
                <div
                  style={{
                    position: "absolute",
                    left: selectionPopover.x,
                    top: selectionPopover.y,
                    zIndex: 50,
                    background: "rgba(17, 17, 17, 0.96)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    borderRadius: 10,
                    padding: 0,
                    color: "#fff",
                    fontSize: 13,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
                    backdropFilter: "blur(8px)",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <button
                    type="button"
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#fff",
                      cursor: "pointer",
                      padding: "6px 8px",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: 1.1,
                      letterSpacing: "0.1px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                    }}
                    onClick={() => {
                      const detail = {
                        file: selectionPopover.file,
                        startLine: selectionPopover.startLine,
                        endLine: selectionPopover.endLine,
                        text: selectionPopover.text,
                      };
                      window.dispatchEvent(
                        new CustomEvent("main-chat-insert-code", { detail })
                      );
                      setSelectionPopover(null);
                    }}
                    title="Add selection to chat"
                  >
                    <span style={{ opacity: 0.95 }}>Add to Chat</span>
                  </button>
                </div>
              )}
              <Editor
                height="calc(100vh - 140px)"
                language={getLanguageFromFilename(activeFile.name)}
                theme={currentSettings.theme}
                value={activeFile.content}
                onChange={handleEditorChange}
                onMount={(editor) => {
                  editorRef.current = editor;
                  // Show a small popover when user selects text.
                  editor.onDidChangeCursorSelection?.(() => {
                    try {
                      const file = activeFileRef.current;
                      if (!file?.name) return;
                      const model = editor.getModel?.();
                      const sel = editor.getSelection?.();
                      if (!model || !sel) return;

                      const selectedText = model.getValueInRange(sel) || "";
                      const hasSelection = selectedText.trim().length > 0;
                      if (!hasSelection) {
                        setSelectionPopover(null);
                        return;
                      }

                      const endPos =
                        sel.getEndPosition?.() ?? {
                          lineNumber: sel.endLineNumber,
                          column: sel.endColumn,
                        };
                      const visible = editor.getScrolledVisiblePosition?.(endPos);
                      const domNode = editor.getDomNode?.();
                      if (!visible || !domNode) return;

                      const rect = domNode.getBoundingClientRect();
                      const x = Math.max(8, Math.min(visible.left, rect.width - 200));
                      const y = Math.max(8, visible.top - 44);

                      setSelectionPopover({
                        x,
                        y,
                        file: file.name,
                        startLine: sel.startLineNumber,
                        endLine: sel.endLineNumber,
                        text: selectedText,
                      });
                    } catch {
                      // ignore
                    }
                  });
                }}
                beforeMount={(monaco) => {
                  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                    noSemanticValidation: true,
                    noSyntaxValidation: true,
                    noSuggestionDiagnostics: true,
                  });
                  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                    noSemanticValidation: true,
                    noSyntaxValidation: true,
                    noSuggestionDiagnostics: true,
                  });
                }}
                options={{
                  readOnly: false,
                  minimap: { enabled: false },
                  fontSize: currentSettings.fontSize,
                  fontFamily: getEditorFontFamily(currentSettings.fontFamily),
                  fontLigatures: ["Fira Code", "JetBrains Mono", "Source Code Pro"].includes(currentSettings.fontFamily),
                  lineNumbers: "on",
                  scrollbar: {
                    vertical: "auto",
                    horizontal: "auto",
                  },
                }}
              />
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-state-content">
            <Code size={64} strokeWidth={1.5} className="empty-state-icon" />
            <h2 className="empty-state-title">No Files Open</h2>
          </div>
        </div>
      )}
    </main>
  </div>
  );
}

export { getCodeview, extractFilePayload };
export default CodeViewComponent;